import React, { useMemo } from "react";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import VideocamIcon from "@mui/icons-material/Videocam";
import DeleteIcon from "@mui/icons-material/Delete";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import {
  MAX_LISTING_PHOTOS,
  MAX_LISTING_VIDEOS,
  MIN_LISTING_PHOTOS,
  ACCEPTED_VIDEO_ACCEPT,
  MAX_VIDEO_SECONDS,
  MAX_VIDEO_BYTES,
} from "../../constants/listingMedia";
import {
  validateImageFileAsync,
  MIN_LISTING_IMAGE_SHORT_EDGE,
} from "../../utils/imageCompression";
import {
  validateVideoFileAsync,
  formatVideoDuration,
  type ListingVideoEntry,
} from "../../utils/videoValidation";
import { parseListingImageKeys, resolveListingImageUrl } from "../../utils";
import {
  parseListingVideoEntries,
  resolveListingVideoUrl,
} from "../../utils/listingMedia";
import "./ListingMediaEditor.css";

export type PendingVideo = {
  file: File;
  durationSeconds: number;
  contentType: string;
};

/** Ordered photo slot: existing S3 key or a newly picked File. */
export type PhotoItem =
  | { kind: "existing"; key: string }
  | { kind: "new"; file: File; localId: string };

export type VideoItem =
  | { kind: "existing"; entry: ListingVideoEntry }
  | { kind: "new"; pending: PendingVideo; localId: string };

export type ListingMediaEditorValue = {
  photos: PhotoItem[];
  videos: VideoItem[];
  removedKeys: string[];
};

type Props = {
  value: ListingMediaEditorValue;
  onChange: (next: ListingMediaEditorValue) => void;
  darkMode?: boolean;
};

let localIdCounter = 0;
function nextLocalId(prefix: string): string {
  localIdCounter += 1;
  return `${prefix}-${localIdCounter}-${Date.now()}`;
}

export function mediaFromListing(
  imageLoc: unknown,
  videoLoc: unknown
): ListingMediaEditorValue {
  return {
    photos: parseListingImageKeys(imageLoc).map((key) => ({
      kind: "existing" as const,
      key,
    })),
    videos: parseListingVideoEntries(videoLoc).map((entry) => ({
      kind: "existing" as const,
      entry,
    })),
    removedKeys: [],
  };
}

/** True when media differs from the snapshot loaded from the server. */
export function isListingMediaDirty(
  current: ListingMediaEditorValue,
  original: ListingMediaEditorValue | null
): boolean {
  if (!original) return false;
  if (current.removedKeys.length > 0) return true;
  if (current.photos.length !== original.photos.length) return true;
  if (current.videos.length !== original.videos.length) return true;
  for (let i = 0; i < current.photos.length; i++) {
    const a = current.photos[i];
    const b = original.photos[i];
    if (!a || !b) return true;
    if (a.kind === "new" || b.kind === "new") return true;
    if (a.key !== b.key) return true;
  }
  for (let i = 0; i < current.videos.length; i++) {
    const a = current.videos[i];
    const b = original.videos[i];
    if (!a || !b) return true;
    if (a.kind === "new" || b.kind === "new") return true;
    if (a.entry.key !== b.entry.key) return true;
  }
  return false;
}

const ListingMediaEditor: React.FC<Props> = ({
  value,
  onChange,
  darkMode = false,
}) => {
  const photoCount = value.photos.length;
  const videoCount = value.videos.length;
  const meetsMinPhotos = photoCount >= MIN_LISTING_PHOTOS;
  const canAddPhoto = photoCount < MAX_LISTING_PHOTOS;
  const canAddVideo = videoCount < MAX_LISTING_VIDEOS;

  const photoUrls = useMemo(() => {
    return value.photos.map((item) => {
      if (item.kind === "existing") {
        return resolveListingImageUrl(item.key, { darkMode });
      }
      return URL.createObjectURL(item.file);
    });
  }, [value.photos, darkMode]);

  const videoUrls = useMemo(() => {
    return value.videos.map((item) => {
      if (item.kind === "existing") {
        return resolveListingVideoUrl(item.entry.key);
      }
      return URL.createObjectURL(item.pending.file);
    });
  }, [value.videos]);

  React.useEffect(() => {
    const revoke = [
      ...value.photos
        .map((item, i) => (item.kind === "new" ? photoUrls[i] : null))
        .filter(Boolean),
      ...value.videos
        .map((item, i) => (item.kind === "new" ? videoUrls[i] : null))
        .filter(Boolean),
    ] as string[];
    return () => {
      for (const url of revoke) URL.revokeObjectURL(url);
    };
  }, [value.photos, value.videos, photoUrls, videoUrls]);

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    const input = e.target;
    if (!files?.length) {
      input.value = "";
      return;
    }

    const room = MAX_LISTING_PHOTOS - photoCount;
    const candidates = Array.from(files).slice(0, Math.max(0, room));
    const valid: PhotoItem[] = [];
    let invalid = 0;

    for (const file of candidates) {
      const result = await validateImageFileAsync(file, {
        minShortEdge: MIN_LISTING_IMAGE_SHORT_EDGE,
      });
      if (result.valid) {
        valid.push({ kind: "new", file, localId: nextLocalId("photo") });
      } else {
        invalid += 1;
      }
    }

    if (invalid > 0) {
      alert(
        `1 or more images are invalid. Vehicle photos must be at least ${MIN_LISTING_IMAGE_SHORT_EDGE}px on the shortest side.`
      );
    }
    if (files.length > room) {
      alert(`You can add at most ${MAX_LISTING_PHOTOS} photos per listing.`);
    }

    if (valid.length > 0) {
      onChange({ ...value, photos: [...value.photos, ...valid] });
    }
    input.value = "";
  };

  const handleRemovePhoto = (index: number) => {
    const item = value.photos[index];
    if (!item) return;
    const photos = value.photos.filter((_, i) => i !== index);
    const removedKeys =
      item.kind === "existing"
        ? [...value.removedKeys, item.key]
        : value.removedKeys;
    onChange({ ...value, photos, removedKeys });
  };

  const handleSetMainPhoto = (index: number) => {
    if (index <= 0) return;
    const photos = [...value.photos];
    const [selected] = photos.splice(index, 1);
    if (!selected) return;
    photos.unshift(selected);
    onChange({ ...value, photos });
  };

  const handleVideoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    const input = e.target;
    if (!files?.length) {
      input.value = "";
      return;
    }

    const room = MAX_LISTING_VIDEOS - videoCount;
    const candidates = Array.from(files).slice(0, Math.max(0, room));
    const valid: VideoItem[] = [];
    const errors: string[] = [];

    for (const file of candidates) {
      const result = await validateVideoFileAsync(file);
      if (result.valid) {
        valid.push({
          kind: "new",
          localId: nextLocalId("video"),
          pending: {
            file,
            durationSeconds: result.durationSeconds,
            contentType: result.contentType,
          },
        });
      } else {
        errors.push(`${file.name}: ${result.reason}`);
      }
    }

    if (files.length > room) {
      errors.push(`You can add at most ${MAX_LISTING_VIDEOS} videos per listing.`);
    }
    if (errors.length > 0) alert(errors.join("\n"));
    if (valid.length > 0) {
      onChange({ ...value, videos: [...value.videos, ...valid] });
    }
    input.value = "";
  };

  const handleRemoveVideo = (index: number) => {
    const item = value.videos[index];
    if (!item) return;
    const videos = value.videos.filter((_, i) => i !== index);
    const removedKeys =
      item.kind === "existing"
        ? [...value.removedKeys, item.entry.key]
        : value.removedKeys;
    onChange({ ...value, videos, removedKeys });
  };

  return (
    <div className="listing-media-editor" data-testid="listing-media-editor">
      <div>
        <h3 className="listing-media-section-title">Photos</h3>
        <p className="listing-media-hint">
          Keep at least {MIN_LISTING_PHOTOS} photos (max {MAX_LISTING_PHOTOS}).
          Click a photo to set it as the main listing image. Removed photos are
          deleted from storage when you save.
        </p>
        <div
          className={`listing-media-count ${meetsMinPhotos ? "met" : "not-met"}`}
        >
          {meetsMinPhotos ? (
            <CheckCircleIcon style={{ fontSize: 18 }} />
          ) : (
            <ErrorIcon style={{ fontSize: 18 }} />
          )}
          {photoCount} / {MIN_LISTING_PHOTOS} minimum
          {photoCount > 0 ? ` · ${photoCount}/${MAX_LISTING_PHOTOS} max` : ""}
        </div>
        <div className="listing-media-grid">
          {value.photos.map((item, index) => (
            <div
              key={item.kind === "existing" ? item.key : item.localId}
              className={`listing-media-item ${darkMode ? "dark" : ""} ${
                index === 0 ? "main" : ""
              }`}
              onClick={() => handleSetMainPhoto(index)}
              role="button"
              tabIndex={0}
              onKeyDown={(ev) => {
                if (ev.key === "Enter" || ev.key === " ") {
                  ev.preventDefault();
                  handleSetMainPhoto(index);
                }
              }}
              title={index === 0 ? "Main photo" : "Click to set as main photo"}
            >
              <img src={photoUrls[index]} alt={`Listing photo ${index + 1}`} />
              <button
                type="button"
                className="remove"
                aria-label={`Remove photo ${index + 1}`}
                onClick={(ev) => {
                  ev.stopPropagation();
                  handleRemovePhoto(index);
                }}
              >
                <DeleteIcon style={{ fontSize: 16 }} />
              </button>
              <div className={`listing-media-badge ${index === 0 ? "main" : ""}`}>
                {index === 0 ? (
                  <StarIcon style={{ fontSize: 12 }} />
                ) : (
                  <StarBorderIcon style={{ fontSize: 12 }} />
                )}
                {index === 0 ? "Main" : "Set main"}
              </div>
            </div>
          ))}
          {canAddPhoto && (
            <label className={`listing-media-add ${darkMode ? "dark" : ""}`}>
              <AddPhotoAlternateIcon style={{ fontSize: 28, marginBottom: 4 }} />
              <span>Add photo</span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/*"
                multiple
                data-testid="edit-photo-input"
                onChange={handlePhotoSelect}
              />
            </label>
          )}
        </div>
      </div>

      <div>
        <h3 className="listing-media-section-title">Videos</h3>
        <p className="listing-media-hint">
          Optional — up to {MAX_LISTING_VIDEOS} videos, max {MAX_VIDEO_SECONDS}s
          and {Math.floor(MAX_VIDEO_BYTES / (1024 * 1024))}MB each (MP4 or WebM).
          Removed videos are deleted from storage when you save.
        </p>
        <div className="listing-media-count met">
          <VideocamIcon style={{ fontSize: 18 }} />
          {videoCount} / {MAX_LISTING_VIDEOS} videos
        </div>
        <div className="listing-media-grid">
          {value.videos.map((item, index) => (
            <div
              key={item.kind === "existing" ? item.entry.key : item.localId}
              className={`listing-media-item ${darkMode ? "dark" : ""}`}
            >
              <video
                src={videoUrls[index]}
                muted
                playsInline
                preload="metadata"
              />
              <button
                type="button"
                className="remove"
                aria-label={`Remove video ${index + 1}`}
                onClick={() => handleRemoveVideo(index)}
              >
                <DeleteIcon style={{ fontSize: 16 }} />
              </button>
              <div className="listing-media-badge">
                {formatVideoDuration(
                  item.kind === "existing"
                    ? item.entry.durationSeconds
                    : item.pending.durationSeconds
                ) || "Video"}
              </div>
            </div>
          ))}
          {canAddVideo && (
            <label className={`listing-media-add ${darkMode ? "dark" : ""}`}>
              <VideocamIcon style={{ fontSize: 28, marginBottom: 4 }} />
              <span>Add video</span>
              <input
                type="file"
                accept={ACCEPTED_VIDEO_ACCEPT}
                multiple
                data-testid="edit-video-input"
                onChange={handleVideoSelect}
              />
            </label>
          )}
        </div>
      </div>
    </div>
  );
};

export default ListingMediaEditor;
